import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * A detailed 3-step wizard tour for the AIChatbot Edit page.
 * Context-aware: only shows steps relevant to the current wizard step.
 */
export const startOnboardingWizardTour = (t: any, step: string) => {
  const driverObj = driver({
    showProgress: true,
    animate: true,
    overlayColor: "rgba(0,0,0,0.75)",
    allowClose: true,
    doneBtnText: t("tour.done", "Done"),
    nextBtnText: t("tour.next", "Next"),
    prevBtnText: t("tour.prev", "Back"),
    steps: [] as any[],
  });

  const steps: any[] = [];

  // ==========================================
  // STEP 1: GENERAL
  // ==========================================
  if (step === "general") {
    // Welcome
    steps.push({
      popover: {
        title: t("tour.wizard.welcome_title"),
        description: t("tour.wizard.welcome_desc"),
      },
    });

    // 3 tabs intro
    steps.push({
      element: ".chatbots-tabs-item:nth-child(1)",
      popover: {
        title: t("tour.wizard.general_title"),
        description: t("tour.wizard.general_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: ".chatbots-tabs-item:nth-child(2)",
      popover: {
        title: t("tour.wizard.training_title"),
        description: t("tour.wizard.training_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: ".chatbots-tabs-item:nth-child(3)",
      popover: {
        title: t("tour.wizard.widget_title"),
        description: t("tour.wizard.widget_desc"),
        side: "bottom",
      },
    });

    // Page title
    steps.push({
      element: ".chatbots-edit-header h1",
      popover: {
        title: t("tour.wizard.general_title"),
        description: t("tour.wizard.general_desc"),
        side: "bottom",
      },
    });

    // Input fields in General
    steps.push({
      element: ".form-group-custom:has(#name)",
      popover: {
        title: t("tour.wizard.name_title"),
        description: t("tour.wizard.name_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".form-group-custom:has(#company_id)",
      popover: {
        title: t("tour.wizard.company_title"),
        description: t("tour.wizard.company_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".form-group-custom:has(#description)",
      popover: {
        title: t("tour.wizard.description_title"),
        description: t("tour.wizard.description_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".form-group-custom:has(#initial_message)",
      popover: {
        title: t("tour.wizard.initial_msg_title"),
        description: t("tour.wizard.initial_msg_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".form-group-custom:has(#allowed_domains)",
      popover: {
        title: t("tour.wizard.domains_title"),
        description: t("tour.wizard.domains_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".form-group-custom:has(#is_active)",
      popover: {
        title: t("tour.wizard.status_title"),
        description: t("tour.wizard.status_desc"),
        side: "right",
      },
    });

    steps.push({
      element: ".chatbots-actions-save",
      popover: {
        title: t("tour.wizard.save_next_title"),
        description: t("tour.wizard.save_next_desc"),
        side: "left",
      },
    });
  }

  // ==========================================
  // STEP 2: TRAINING
  // ==========================================
  if (step === "training") {
    // Profile & Guardrails highlights
    steps.push({
      element: ".chatbots-double-tabs-link.active",
      popover: {
        title: t("tour.wizard.profile_nav_title"),
        description: t("tour.wizard.profile_nav_desc"),
        side: "bottom",
      },
      onHighlighted: () => {
        // Ensure profile tab is active
        const tabs = document.querySelectorAll(".chatbots-double-tabs-link");
        if (tabs[0]) (tabs[0] as HTMLElement).click();
      },
    });

    steps.push({
      element: ".mb-2:has(#persona_input)",
      popover: {
        title: t("tour.wizard.persona_title"),
        description: t("tour.wizard.persona_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".mb-2:has(#tone_select)",
      popover: {
        title: t("tour.wizard.tone_title"),
        description: t("tour.wizard.tone_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".mb-2:has(#language_select)",
      popover: {
        title: t("tour.wizard.language_title"),
        description: t("tour.wizard.language_desc"),
        side: "right",
      },
    });
    steps.push({
      element: ".mt-3:has(#banned_topics_section)",
      popover: {
        title: t("tour.wizard.guardrails_title"),
        description: t("tour.wizard.guardrails_desc"),
        side: "top",
      },
    });

    // Switch to Knowledge Sources
    steps.push({
      element: ".chatbots-double-tabs li:nth-child(2) .chatbots-double-tabs-link",
      popover: {
        title: t("tour.wizard.sources_nav_title"),
        description: t("tour.wizard.sources_nav_desc"),
        side: "bottom",
      },
      onHighlighted: () => {
        const tabs = document.querySelectorAll(".chatbots-double-tabs-link");
        if (tabs[1]) (tabs[1] as HTMLElement).click();
      },
    });

    // Table Headers
    steps.push({
      element: "#source_type_header",
      popover: { title: t("tour.wizard.source_type_title"), description: t("tour.wizard.source_type_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_title_header",
      popover: { title: t("tour.wizard.source_title_title"), description: t("tour.wizard.source_title_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_lang_header",
      popover: { title: t("tour.wizard.source_lang_title"), description: t("tour.wizard.source_lang_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_status_header",
      popover: { title: t("tour.wizard.source_status_title"), description: t("tour.wizard.source_status_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_stats_header",
      popover: { title: t("tour.wizard.source_stats_title"), description: t("tour.wizard.source_stats_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_updated_header",
      popover: { title: t("tour.wizard.source_updated_title"), description: t("tour.wizard.source_updated_desc"), side: "bottom" }
    });
    steps.push({
      element: "#source_actions_header",
      popover: { title: t("tour.wizard.source_actions_title"), description: t("tour.wizard.source_actions_desc"), side: "bottom" }
    });

    // Train Data Button - Trigger Modal
    steps.push({
      element: "#train_data_btn",
      popover: {
        title: t("tour.wizard.add_source_btn_title"),
        description: t("tour.wizard.add_source_btn_desc"),
        side: "left",
      },
      onHighlighted: () => {
        if (!document.querySelector(".modal-content")) {
          const btn = document.getElementById("train_data_btn");
          if (btn) btn.click();
        }
      },
    });

    // 1. Entire Modal - Overview
    steps.push({
      element: ".modal-content",
      popover: {
        title: t("tour.wizard.modal_title"),
        description: t("tour.wizard.modal_desc"),
        side: "right",
      },
    });

    // 2. TEXT CONTENT FLOW
    // Text Tab Highlight
    steps.push({
      element: "#text_tab",
      popover: {
        title: t("tour.wizard.modal_text_title"),
        description: t("tour.wizard.modal_text_tab_desc"),
        side: "bottom",
      },
      onHighlighted: () => {
        const tab = document.getElementById("text_tab");
        if (tab) tab.click();
      }
    });

    // Fields while on Text Tab
    steps.push({
      element: "#modal_source_title",
      popover: {
        title: t("tour.wizard.modal_source_title_title"),
        description: t("tour.wizard.modal_source_title_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: "#modal_source_lang",
      popover: {
        title: t("tour.wizard.modal_source_lang_title"),
        description: t("tour.wizard.modal_source_lang_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: "#modal_source_content",
      popover: {
        title: t("tour.wizard.modal_text_title"),
        description: t("tour.wizard.modal_text_desc"),
        side: "top",
      },
    });
    steps.push({
      element: "#add_ingest_btn",
      popover: {
        title: t("tour.wizard.modal_submit_title"),
        description: t("tour.wizard.modal_text_submit_desc"),
        side: "top",
      },
    });

    // 3. FILE UPLOAD FLOW
    // File Tab Highlight (Switch Tab)
    steps.push({
      element: "#file_tab",
      popover: {
        title: t("tour.wizard.modal_file_title"),
        description: t("tour.wizard.modal_file_tab_desc"),
        side: "bottom",
      },
      onHighlighted: () => {
        const tab = document.getElementById("file_tab");
        if (tab) tab.click();
      }
    });

    // Fields while on File Tab
    steps.push({
      element: "#modal_source_title",
      popover: {
        title: t("tour.wizard.modal_source_title_title"),
        description: t("tour.wizard.modal_file_title_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: "#modal_source_lang",
      popover: {
        title: t("tour.wizard.modal_source_lang_title"),
        description: t("tour.wizard.modal_file_lang_desc"),
        side: "bottom",
      },
    });
    steps.push({
      element: "#modal_file_dropzone",
      popover: {
        title: t("tour.wizard.modal_file_area_title"),
        description: t("tour.wizard.modal_file_desc"),
        side: "top",
      },
    });
    steps.push({
      element: "#add_ingest_btn",
      popover: {
        title: t("tour.wizard.modal_submit_title"),
        description: t("tour.wizard.modal_file_submit_desc"),
        side: "top",
      },
    });

    // 4. Proceed to Widget Design
    steps.push({
      element: ".chatbots-actions-save",
      popover: {
        title: t("tour.wizard.save_next_title"),
        description: t("tour.wizard.save_next_desc"),
        side: "left",
      },
      onHighlighted: () => {
        const closeBtn = document.querySelector(".modal-header .btn-close");
        if (closeBtn) {
          (closeBtn as HTMLElement).click();
        }
      }
    });
  }

  // ==========================================
  // STEP 3: WIDGET
  // ==========================================
  if (step === "widget-settings") {
    steps.push({
      element: ".widget-builder-settings .chatbots-double-tabs",
      popover: {
        title: t("tour.wizard.widget_tabs_title"),
        description: t("tour.wizard.widget_tabs_desc"),
        side: "bottom",
      },
    });

    // 1. Launcher Tab
    steps.push({
      element: ".widget-tab-launcher",
      popover: {
        title: t("tour.wizard.widget_launcher_title"),
        description: t("tour.wizard.widget_launcher_desc"),
        side: "bottom"
      },
      onHighlighted: () => {
        const tab = document.querySelector(".widget-tab-launcher .chatbots-double-tabs-link");
        if (tab) (tab as HTMLElement).click();
      }
    });

    // 2. Header Tab
    steps.push({
      element: ".widget-tab-header",
      popover: {
        title: t("tour.wizard.widget_header_title"),
        description: t("tour.wizard.widget_header_desc"),
        side: "bottom"
      },
      onHighlighted: () => {
        const tab = document.querySelector(".widget-tab-header .chatbots-double-tabs-link");
        if (tab) (tab as HTMLElement).click();
      }
    });

    // 3. Input Tab
    steps.push({
      element: ".widget-tab-input",
      popover: {
        title: t("tour.wizard.widget_input_title"),
        description: t("tour.wizard.widget_input_desc"),
        side: "bottom"
      },
      onHighlighted: () => {
        const tab = document.querySelector(".widget-tab-input .chatbots-double-tabs-link");
        if (tab) (tab as HTMLElement).click();
      }
    });

    // 4. Messages Tab
    steps.push({
      element: ".widget-tab-messages",
      popover: {
        title: t("tour.wizard.widget_messages_title"),
        description: t("tour.wizard.widget_messages_desc"),
        side: "bottom"
      },
      onHighlighted: () => {
        const tab = document.querySelector(".widget-tab-messages .chatbots-double-tabs-link");
        if (tab) (tab as HTMLElement).click();
      }
    });

    // 5. Advanced Tab
    steps.push({
      element: ".widget-tab-advanced",
      popover: {
        title: t("tour.wizard.widget_advanced_title"),
        description: t("tour.wizard.widget_advanced_desc"),
        side: "bottom"
      },
      onHighlighted: () => {
        const tab = document.querySelector(".widget-tab-advanced .chatbots-double-tabs-link");
        if (tab) (tab as HTMLElement).click();
      }
    });

    // Preview
    steps.push({
      element: ".widget-builder-preview",
      popover: {
        title: t("tour.wizard.preview_title"),
        description: t("tour.wizard.preview_desc"),
        side: "left",
      },
    });

    // Finish
    steps.push({
      element: ".chatbots-actions-save",
      popover: {
        title: t("tour.wizard.finish_title"),
        description: t("tour.wizard.finish_desc"),
        side: "left",
      },
    });
  }

  driverObj.setSteps(steps);
  driverObj.drive();
};

export const startChatbotTour = (t: any) => {
  const steps: any[] = [];

  // Only add steps if there is at least one chatbot card visible
  const hasChatbots = document.querySelector(".chatbot-card");

  if (hasChatbots) {
    // 1. Focus directly on the first card for general info
    steps.push({
      element: ".chatbot-card:first-child",
      popover: {
        title: t("tour.card_title"),
        description: t("tour.card_desc"),
        side: "right",
      },
    });

    // 2. Messages Button
    steps.push({
      element: ".chatbot-card:first-child .btn-messages",
      popover: {
        title: t("tour.card_messages_title", "Chat History"),
        description: t("tour.card_messages_desc", "View all past conversations and customer interactions with this specific bot."),
        side: "top",
      },
    });

    // 3. Test Button
    steps.push({
      element: ".chatbot-card:first-child .btn-test",
      popover: {
        title: t("tour.card_test_title", "Test Assistant"),
        description: t("tour.card_test_desc", "Open a private chat window to test your bot's knowledge and current training."),
        side: "top",
      },
    });

    // 4. Add to Website Button
    steps.push({
      element: ".chatbot-card:first-child .btn-widget",
      popover: {
        title: t("tour.card_widget_title", "Add to Website"),
        description: t("tour.card_widget_desc", "Get the embedding code and script to easily integrate this chatbot into your website."),
        side: "top",
      },
    });

    // 5. Live View Button
    steps.push({
      element: ".chatbot-card:first-child .btn-preview",
      popover: {
        title: t("tour.card_preview_title", "Live View"),
        description: t("tour.card_preview_desc", "See how the chatbot widget will actually look and behave on a live website environment."),
        side: "top",
      },
    });

    // 6. Refresh Training Button
    steps.push({
      element: ".chatbot-card:first-child .btn-clear-cache",
      popover: {
        title: t("tour.card_refresh_title", "Refresh Training"),
        description: t("tour.card_refresh_desc", "Clear the bot's temporary memory to ensure your latest training data is active immediately."),
        side: "top",
      },
    });

    // 7. Edit Button
    steps.push({
      element: ".chatbot-card:first-child .btn-edit",
      popover: {
        title: t("tour.card_edit_title", "Edit & Train"),
        description: t("tour.card_edit_desc", "Go to the advanced editor to change the bot's personality, training documents, and design."),
        side: "top",
      },
    });
  }

  // If no chatbots exist, we should at least show a welcome/empty state step
  if (steps.length === 0) {
    steps.push({
      element: ".chatbots-page",
      popover: {
        title: t("tour.welcome_title"),
        description: t("tour.welcome_desc"),
      },
    });
    steps.push({
      element: 'button[name="create_chatbot_btn"]',
      popover: {
        title: t("tour.create_title"),
        description: t("tour.create_desc"),
        side: "left",
      },
    });
  }

  const driverObj = driver({
    showProgress: true,
    animate: true,
    overlayColor: "rgba(0,0,0,0.75)",
    allowClose: true,
    doneBtnText: t("tour.done", "Done"),
    nextBtnText: t("tour.next", "Next"),
    prevBtnText: t("tour.prev", "Back"),
    steps,
  });

  driverObj.drive();
};

export const startEditTour = (t: any, setActiveTab: (tab: string) => void) => {
  const driverObj = driver({
    showProgress: true,
    animate: true,
    overlayColor: "rgba(0,0,0,0.75)",
    allowClose: true,
    doneBtnText: t("tour.done", "Done"),
    nextBtnText: t("tour.next", "Next"),
    prevBtnText: t("tour.prev", "Back"),
    steps: [
      {
        element: ".chatbots-tabs-container",
        popover: {
          title: t("tour.tabs.title"),
          description: t("tour.tabs.desc"),
          side: "bottom",
        },
      },
      {
        element: ".chatbots-tabs-item:nth-child(1)",
        popover: {
          title: t("tour.tab_general.title"),
          description: t("tour.tab_general.desc"),
          side: "bottom",
        },
        onHighlighted: () => {
          setActiveTab("general");
        },
      },
      {
        element: ".chatbots-tabs-item:nth-child(2)",
        popover: {
          title: t("tour.tab_training.title"),
          description: t("tour.tab_training.desc"),
          side: "bottom",
        },
        onHighlighted: () => {
          setActiveTab("training");
        },
      },
      {
        element: ".chatbots-tabs-item:nth-child(3)",
        popover: {
          title: t("tour.tab_widget.title"),
          description: t("tour.tab_widget.desc"),
          side: "bottom",
        },
        onHighlighted: () => {
          setActiveTab("widget-settings");
        },
      },
      {
        element: ".chatbots-tabs-item:nth-child(5)",
        popover: {
          title: t("tour.tab_advanced_settings.title", "Advance Settings"),
          description: t("tour.tab_advanced_settings.desc", "Configure advanced AI settings and parameters."),
          side: "bottom",
        },
        onHighlighted: () => {
          setActiveTab("ai-config");
        },
      },

      {
        element: ".chatbots-actions-save",
        popover: {
          title: t("tour.save_btn.title"),
          description: t("tour.save_btn.desc"),
          side: "left",
        },
      },
    ],
  });

  driverObj.drive();
};
